import { type FormEvent, useRef, useState } from 'react';
import { type Mode } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'ahooks';
import { Link, Typography } from '@mui/material';
import { GoogleCaptcha, type GoogleCaptchaRef } from '@/components/biz/google-captcha';
import { FormCheckbox } from '@/components/form/form-checkbox';
import { FormCountrySelect } from '@/components/form/form-country-select';
import { FormText } from '@/components/form/form-text';
import { FormUsernameInput } from '@/components/form/form-username-input';
import { StepBox } from '@/components/layout/step-box';
import { Continue } from '@/components/ui/continue';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCore } from '@/hooks/use-core';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useCustom } from '@/hooks/use-custom';
import { useLidForm } from '@/hooks/use-lid-form';
import { useRules } from '@/hooks/use-rules';
import { KoProtocol1 } from './ko-protocol/ko-protocol-1';

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  areaCode: string;
  confirmed: boolean;
  country: string;
}

export const StepInputUserInfo: React.FC = () => {
  const { t, i18n } = useTranslation();
  // add a small delay between loading and requesting recaptcha to avoid flickering of button loading
  const [buttonLoading, setButtonLoading] = useState(false);
  const rules = useRules();
  const { isSingleAuthType } = useCustom();
  const recaptchaRef = useRef<GoogleCaptchaRef>(null);

  const { setFlow, lenovoId, lang } = useCore();
  const { authType, email, areaCode, phone, firstName, lastName, country, googleVersion, setBizState } = useBizState();
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [mode, setMode] = useState<Mode>('onSubmit');
  const { control, setError, setFocus, setValue, clearErrors, trigger, getValues } = useLidForm({
    mode,
    defaultValues: {
      email,
      areaCode,
      phone,
      firstName,
      lastName,
      country,
      confirmed: false,
    },
  });

  const toggleAuthType = () => {
    const type = authType === 'email' ? 'phone' : 'email';
    setValue(authType, '');
    clearErrors(authType);
    setBizState({ authType: type });
    setTimeout(() => setFocus(type));
  };

  const { loading, run } = useCoreRequest(
    (params: RegisterFormValues) => {
      const userName = authType === 'email' ? params.email : params.phone;
      const areaCode = authType === 'phone' ? params.areaCode : '';
      return lenovoId.registerVerifyUserName({
        userName,
        areaCode,
        lang,
        googleToken: recaptchaToken,
        googleVersion,
        bid: window.bid,
      });
    },
    {
      manual: true,
      ready: !recaptchaLoading,
      onBefore: () => {
        setButtonLoading(true);
      },
      onSuccess: (_data, [params]) => {
        setButtonLoading(false);
        setFlow(Flow.REGISTER_STEP_INPUT_CODE, {
          state: {
            userName: authType === 'email' ? params.email : params.phone,
            areaCode: params.areaCode,
            firstName: params.firstName,
            lastName: params.lastName,
            email: params.email,
            phone: params.phone,
            country: params.country,
          },
        });
      },
      onError: (errorCode) => {
        setButtonLoading(false);
        switch (errorCode) {
          case ErrorCode.UsernameExists:
            setError(authType, t('account_exists_error'));
            recaptchaRef.current?.reload();
            return true;

          case ErrorCode.InvalidUsername:
            if (authType === 'email') setError('email', t('email_format_error'));
            else setError('phone', t('phone_format_error'));
            recaptchaRef.current?.reload();
            return true;
        }
      },
    },
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isRecaptchaValid = recaptchaRef.current?.validation();
    trigger([authType, 'firstName', 'lastName', 'country', 'confirmed'], { shouldFocus: true }).then((isValid) => {
      setMode('onChange');
      if (isValid && isRecaptchaValid) {
        run(getValues());
      }
    });
  };

  const handleRecaptchaLoading = (loading: boolean) => {
    setRecaptchaLoading(loading);
  };

  const handleRecaptchaToken = (token: string) => {
    setRecaptchaToken(token);
  };

  useUpdateEffect(() => {
    if (!recaptchaLoading) {
      setButtonLoading(false);
    }
  }, [recaptchaLoading]);

  return (
    <StepBox title={t('account_creation_title')}>
      {!isSingleAuthType && (
        <div>
          <Typography component="span" className="align-middle" sx={{ mr: 1 }}>
            {authType === 'email' ? t('email_registration_prompt') : t('phone_registration_prompt')}
          </Typography>
          <Link
            aria-label={authType === 'email' ? t('register_with_phone') : t('register_with_email')}
            component="button"
            type="button"
            disabled={loading}
            color={loading ? 'textDisabled' : 'primary'}
            className="align-middle"
            onClick={toggleAuthType}
          >
            {authType === 'email' ? t('phone_number') : t('email')}
          </Link>
        </div>
      )}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <FormUsernameInput control={control} type={authType} />

        <div className="flex flex-col gap-4 sm:flex-row">
          <FormText trim name="firstName" label={t('first_name')} control={control} rules={rules.firstName} />
          <FormText trim name="lastName" label={t('last_name')} control={control} rules={rules.lastName} />
        </div>

        <FormCountrySelect control={control} />

        <div>
          {i18n.language === 'ko' ? (
            <KoProtocol1 control={control} />
          ) : (
            <FormCheckbox
              name="confirmed"
              label={t('age_confirmation_checkbox')}
              control={control}
              rules={{ required: t('confirmation_required') }}
            />
          )}
        </div>

        <GoogleCaptcha
          onLoadingChange={handleRecaptchaLoading}
          onTokenChange={handleRecaptchaToken}
          ref={recaptchaRef}
        />

        <Continue loading={loading || recaptchaLoading || buttonLoading} />
      </form>

      <div className="mt-4 flex items-center justify-center">
        <span>{t('account_existing_user_prompt')}</span>
        <Link
          component="button"
          type="button"
          sx={{ ml: 1 }}
          onClick={() => setFlow(Flow.LOGIN_STEP_INPUT_USERNAME, { resetBiz: true })}
        >
          {t('sign_in')}
        </Link>
      </div>
    </StepBox>
  );
};
